import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { IgButton, IgTypography } from "@intelligems/ig-theme";
import { ExperienceDto, ExperienceListDto } from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useIsMutating } from "@tanstack/react-query";

import { useConfiguration } from "../../api/configurations/use-configuration";
import { useEndExperience } from "../../api/experience-actions/end-experience";
import { getExperienceDtoFromFe } from "../../api/formatters/from-dto/experiences/experiences";
import { EndShippingTestDialog } from "../../components/Dialogs/EndShippingTestDialog";
import { MarketPriceUpdaterDialog } from "../../features/experienceTests/components/dialogs/MarketPriceUpdaterDialog";
import { MarketUpdateConfirmDialog } from "../../features/experienceTests/components/dialogs/MarketUpdateConfirmDialog";
import { PriceUpdaterDialog } from "../../features/experienceTests/components/dialogs/PriceUpdaterDialog";
import { selectedExperience } from "../../store/slices/experiences";
import { getDialog, isDialogOpen, setDialog } from "../../store/slices/global";
import { ExperienceFe } from "../../types/schemas/experiences";
import { dateTimeConverter } from "../../utils/dateConverters";

import { ResourceListMenu } from "./Utils/ResourceListMenu";
import { TimezoneLastUpdatedInfo } from "./Utils/TimezoneLastUpdatedInfo";
import { EditTestGroupsDialog } from "./EditTestGroupsDialog";

const PREFIX = "DashboardSimpleHeader";

const classes = {
  text: `${PREFIX}-text`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .${classes.text}`]: {
    fontSize: "22px",
    fontWeight: 600,
    marginBottom: "24px",
    letterSpacing: ".50px",
  },
}));

interface DashboardHeaderSimpleProps {
  activeExperiences: ExperienceListDto[] | ExperienceDto[];
  latestRunTs: number | undefined;
}

export const DashboardHeaderSimple = ({
  activeExperiences,
  latestRunTs,
}: DashboardHeaderSimpleProps) => {
  const currExperience = useSelector(selectedExperience);
  const dispatch = useDispatch();

  const endExperience = useEndExperience(true, currExperience.id);

  const isEndExperimentMutating = useIsMutating({
    mutationKey: ["endExperience"],
  });

  const isEditTestGroupsOpen = useSelector(isDialogOpen("editTestGroups"));

  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");

  const marketPriceUpdater = useSelector(getDialog("marketPriceUpdater"));
  const marketUpdateConfirm = useSelector(getDialog("marketUpdateConfirm"));

  const priceUpdaterDispatch = (action: string) => {
    dispatch(
      setDialog({
        dialog: "priceUpdater",
        open: true,
        props: { action: action },
      })
    );
  };

  const marketPriceUpdaterDispatch = (action: string) => {
    dispatch(
      setDialog({
        dialog: "marketPriceUpdater",
        open: true,
        props: { action: action },
      })
    );
  };

  const marketOrPriceUpdaterDispatch = (
    action: string,
    experience: ExperienceFe
  ) => {
    if (
      experience.currency?.code &&
      storeCurrencyConfig?.value?.code !== experience.currency?.code
    )
      marketPriceUpdaterDispatch(action);
    else priceUpdaterDispatch(action);
  };

  const onExpButtonClick = () => {
    if (currExperience.testTypes.hasTestShipping) {
      dispatch(setDialog({ dialog: "endShippingTest", open: true }));
    } else if (currExperience.testTypes.hasTestPricing) {
      marketOrPriceUpdaterDispatch("end", currExperience);
    } else {
      // Campaign Test / Content
      endExperience.mutate({
        body: { variationId: undefined },
        queryParams: undefined,
        pathParams: {
          experienceId: currExperience.id!,
          name: currExperience.name!,
          action: "end",
        },
      });
    }
  };

  return (
    <StyledGrid item>
      {currExperience && (
        <PriceUpdaterDialog
          noRedirect={true}
          currExperience={
            getExperienceDtoFromFe(currExperience) as ExperienceDto
          }
        />
      )}

      {currExperience && (
        <EndShippingTestDialog
          currExperience={
            getExperienceDtoFromFe(currExperience) as ExperienceDto
          }
        />
      )}
      {marketPriceUpdater.isOpen && currExperience && (
        <MarketPriceUpdaterDialog
          currExperience={
            getExperienceDtoFromFe(currExperience) as ExperienceDto
          }
        />
      )}
      {marketUpdateConfirm.isOpen && currExperience && (
        <MarketUpdateConfirmDialog
          currExperience={
            getExperienceDtoFromFe(currExperience) as ExperienceDto
          }
        />
      )}
      <IgTypography className={classes.text}>Analytics Dashboard</IgTypography>

      <Grid
        item
        container
        direction={"column"}
        rowSpacing={1}
        columnSpacing={1}
      >
        <ResourceListMenu
          currentResource={currExperience}
          activeResources={activeExperiences}
          resourceType={"experiences"}
        />
        <Grid item container direction={"row"} justifyContent={"space-between"}>
          <Grid item xs={5}>
            <IgTypography sx={{ fontSize: "15px", letterSpacing: ".50px" }}>
              {currExperience.description} from{" "}
              {dateTimeConverter(
                currExperience.startedAtTs,
                undefined,
                undefined,
                true
              )}{" "}
              -{" "}
              {dateTimeConverter(
                currExperience.endedAtTs,
                undefined,
                undefined,
                true
              )}
            </IgTypography>
          </Grid>
          <TimezoneLastUpdatedInfo
            showLastUpdatedTime={currExperience.status === "started"}
            latestRunTs={latestRunTs}
          />
        </Grid>
        <Grid
          container
          item
          direction={"row"}
          columnSpacing={2}
          xs={6}
          alignItems={"center"}
        >
          {!currExperience.endedAtTs && (
            <Grid item>
              <IgButton
                loading={Boolean(isEndExperimentMutating)}
                onClick={onExpButtonClick}
                variant={"outlined"}
                size={"small"}
                color={"error"}
              >
                Stop Test
              </IgButton>
            </Grid>
          )}
          {!currExperience.endedAtTs && (
            <Grid item>
              <IgButton
                size={"small"}
                variant={"contained"}
                onClick={() => {
                  dispatch(setDialog({ dialog: "editTestGroups", open: true }));
                }}
              >
                Edit Groups
              </IgButton>
            </Grid>
          )}
        </Grid>
      </Grid>
      <EditTestGroupsDialog editTestGroupsDialog={isEditTestGroupsOpen} />
    </StyledGrid>
  );
};
